/**
 * Realistic Container Renderer for Standard Template Preview
 * Simplified container rendering with realistic element data
 */

import type { ContainerElement } from "@/features/invoice-template/types/template.types";
import { RealisticElementRenderer } from "./realistic-element-renderer";

interface RealisticContainerRendererProps {
  container: ContainerElement;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
}

export function RealisticContainerRenderer({
  container,
  fontFamily,
  fontSize,
  fontColor,
}: RealisticContainerRendererProps) {
  if (!container.visible) {
    return null;
  }

  const { rows, columns } = container;
  const children = Array.isArray(container.children) ? container.children : [];
  const gap = container.gap ?? 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerEl = container as any;

  // Handle borders
  const borderLeft = containerEl.border_left ?? 0;
  const borderTop = containerEl.border_top ?? 0;
  const borderRight = containerEl.border_right ?? 0;
  const borderBottom = containerEl.border_bottom ?? 0;
  const borderColor = containerEl.borderColor || '#000000';

  return (
    <div
      style={{
        paddingTop: container.paddingTop || 0,
        paddingBottom: container.paddingBottom || 0,
        paddingLeft: container.paddingLeft || 0,
        paddingRight: container.paddingRight || 0,
        backgroundColor: containerEl.backgroundColor,
        borderLeft: borderLeft > 0 ? `${borderLeft}px solid ${borderColor}` : undefined,
        borderTop: borderTop > 0 ? `${borderTop}px solid ${borderColor}` : undefined,
        borderRight: borderRight > 0 ? `${borderRight}px solid ${borderColor}` : undefined,
        borderBottom: borderBottom > 0 ? `${borderBottom}px solid ${borderColor}` : undefined,
        height: "100%",
      }}
    >
      {/* Native CSS Grid */}
      <div
        className="grid h-full"
        style={{
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gridTemplateRows: container.rowHeights?.join(" ") || `repeat(${rows}, 1fr)`,
          gap: `${gap}px`,
        }}
      >
        {Array.from({ length: rows * columns }).map((_, index) => {
          const row = Math.floor(index / columns);
          const col = index % columns;
          const cellKey = `${row}-${col}`;
          
          // Find child for this cell
          const child = children.find((c) => c.cellRow === row && c.cellColumn === col);
          
          // Get cell background color from child
          const cellBgColor = child?.backgroundColor || 'transparent';

          if (!child || !child.element) {
            return <div key={cellKey} style={{ backgroundColor: cellBgColor }} />;
          }

          const element = child.element;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const elementBgColor = (element as any).backgroundColor || cellBgColor;
          
          // Get alignment ONLY from element properties, ignore cellAlignments
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const cellVerticalAlign = (element as any).verticalAlign || 'top';
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const cellHorizontalAlign = (element as any).horizontalAlign || 'left';
          
          // Map alignment values to CSS Grid properties
          const getJustifyItems = (align: string) => {
            switch (align) {
              case 'left': return 'start';
              case 'center': return 'center';
              case 'right': return 'end';
              case 'stretch': return 'stretch';
              default: return 'start';
            }
          };
          
          const getAlignItems = (align: string) => {
            switch (align) {
              case 'top': return 'start';
              case 'middle': return 'center';
              case 'bottom': return 'end';
              case 'stretch': return 'stretch';
              default: return 'start';
            }
          };

          // Render nested container
          if (element.type === "container") {
            return (
              <div 
                key={cellKey}
                style={{
                  backgroundColor: elementBgColor,
                  display: 'grid',
                  justifyItems: getJustifyItems(cellHorizontalAlign),
                  alignItems: getAlignItems(cellVerticalAlign),
                  width: '100%',
                  height: '100%',
                }}
              >
                <RealisticContainerRenderer
                  container={element as ContainerElement}
                  fontFamily={fontFamily}
                  fontSize={fontSize}
                  fontColor={fontColor}
                />
              </div>
            );
          }

          // Render regular element
          return (
            <div 
              key={cellKey}
              style={{
                backgroundColor: elementBgColor,
                display: 'grid',
                justifyItems: getJustifyItems(cellHorizontalAlign),
                alignItems: getAlignItems(cellVerticalAlign),
                width: '100%',
                height: '100%',
              }}
            >
              <RealisticElementRenderer
                element={element}
                globalFontFamily={fontFamily}
                globalFontSize={fontSize}
                globalFontColor={fontColor}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
